import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  RegularModal,
  TimePicker,
} from '@/components_deprecated'
import { DetectBlur } from '@/containers/util-containers'
import { Field, Form } from '..'
import type { HolidayAllowanceNode } from '@/modules/companies/types.graphql'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import { notBlankValidation } from '@/utils/forms'
import styled from 'styled-components'
import { time } from '@/lib/time'
import { parseISO } from 'date-fns'

const TimePickers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
`

export interface HolidayAllowanceForm {
  day: Date
  start: Date
  end: Date
  salaryCodeId: string | null
  override: boolean
  repeatAnnually: boolean
}

interface HolidayAllowancePromptProps {
  initialData?: HolidayAllowanceNode
  onSubmit: PromptResolve<HolidayAllowanceForm | null>
}

export const HolidayAllowancePrompt: React.FC<HolidayAllowancePromptProps> = ({
  initialData,
  onSubmit,
}) => {
  const translations = useTranslate({
    form: {
      label: {
        date: 'common.date',
        salaryCode: 'common.salary-code',
        override: 'salary.override-other-addons',
        repeatAnnually: 'common.repeat-annually',
        timeInterval: 'common.time-interval',
      },
      placeholder: {
        id: 'settings.external-id-in-salary-system',
      },
    },

    addHolidayAllowance: 'settings.add-holiday-allowance',
    editHolidayAllowance: 'settings.edit-holiday-allowance',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      alreadyHolidayAllowanceOnDay:
        'settings.validation.already-holiday-allowance-on-day',
      required: 'common.required',
    },
  })

  const editing = typeof initialData !== 'undefined'

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<HolidayAllowanceForm>({
    values: initialData
      ? {
          day: parseISO(initialData.day),
          start: time(initialData?.start ?? '00:00').toDate(new Date()),
          end: time(initialData?.end ?? '00:00').toDate(new Date()),
          salaryCodeId: initialData.salaryCode?.id ?? null,
          override: initialData.override ?? false,
          repeatAnnually: initialData.repeatAnnually ?? false,
        }
      : {
          salaryCodeId: '',
          day: new Date(),
          start: time('00:00').toDate(new Date()),
          end: time('00:00').toDate(new Date()),
          override: false,
          repeatAnnually: false,
        },
    validators: {
      salaryCodeId: value =>
        notBlankValidation(value) ? null : translations.validation.required,
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={
          editing
            ? translations.editHolidayAllowance
            : translations.addHolidayAllowance
        }
        submitText={editing ? translations.update : translations.create}
        submitDisabled={!formValid}
        width="400px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <Form>
          <Field>
            <label>{translations.form.label.date}</label>
            <DatePicker
              value={form.day}
              noWeekNums
              dateErrorText={errors.day ?? undefined}
              fullWidth
              onChange={handler('day')}
              placeholder={translations.form.placeholder.id}
              grayed
            />
          </Field>

          <Field>
            <label>{translations.form.label.timeInterval}</label>
            <TimePickers>
              <TimePicker
                width="100%"
                disableWheel
                value={form.start}
                onChange={handler('start')}
                grayed
              />

              <TimePicker
                width="100%"
                disableWheel
                value={form.end}
                onChange={handler('end')}
                error={errors.end ?? errors.start}
                grayed
              />
            </TimePickers>
          </Field>

          <Field>
            <label>{translations.form.label.salaryCode}</label>
            <SalaryCodeSelect
              error={errors.salaryCodeId}
              fullWidth
              value={form.salaryCodeId}
              onChange={handler('salaryCodeId')}
              grayed
            />
          </Field>

          <Field>
            <label>{translations.form.label.override}</label>
            <Checkbox
              checked={form.override}
              onChange={handler('override')}
              alignLeft
              grayed
            />
          </Field>

          <Field>
            <label>{translations.form.label.repeatAnnually}</label>
            <Checkbox
              checked={form.repeatAnnually}
              onChange={handler('repeatAnnually')}
              alignLeft
              grayed
            />
          </Field>
        </Form>
      </RegularModal>
    </DetectBlur>
  )
}
